import {
  courseHandicap,
  groupRelativePlayingHandicap,
  netScoresFromGross,
  playingHandicap,
} from '@/lib/games/handicap';
import { computeNassau, computeSkins, type SkinsPlayer } from '@/lib/games/scoring';
import type { CourseHole } from '@/lib/db/types';
import { getHandicapAllowance, getStakeConfig } from './stakes-helpers';
import type { RoundContext, RoundPlayer } from './types';

export function strokeIndexes(holes: CourseHole[]): number[] {
  return [...holes].sort((a, b) => a.hole_num - b.hole_num).map((h) => h.stroke_index);
}

function allowanceFor(ctx: RoundContext, formatId: string): number | undefined {
  return getHandicapAllowance(getStakeConfig(ctx.stakes, formatId), formatId);
}

export function buildSkinsPlayers(
  ctx: RoundContext,
  formatId: string,
  playerKeys?: string[],
): SkinsPlayer[] {
  const sis = strokeIndexes(ctx.holes);
  const allowance = allowanceFor(ctx, formatId);
  const keys = playerKeys?.length ? playerKeys : ctx.players.map((p) => p.playerKey);
  return keys.map((key) => {
    const p = ctx.players.find((x) => x.playerKey === key);
    if (!p) return { id: key, gross: Array(18).fill(null), net: Array(18).fill(null) };
    const gross = ctx.scores[key] || Array(ctx.holes.length).fill(null);
    const ph = ctx.players.length >= 2
      ? groupRelativePlayingHandicap(ctx.players, p, formatId, allowance)
      : playingHandicap(p.courseHcp, formatId, allowance);
    const net = netScoresFromGross(gross, ph, sis);
    return { id: key, gross, net };
  });
}

export function grossToPar(ctx: RoundContext, playerKey: string, useNet = false): number | null {
  const p = ctx.players.find((x) => x.playerKey === playerKey);
  if (!p) return null;
  const gross = ctx.scores[playerKey] || [];
  const sis = strokeIndexes(ctx.holes);
  const allowance = allowanceFor(ctx, useNet ? 'net-stroke' : 'stroke');
  const formatId = useNet ? 'net-stroke' : 'stroke';
  const ph = useNet && ctx.players.length >= 2
    ? groupRelativePlayingHandicap(ctx.players, p, formatId, allowance)
    : playingHandicap(p.courseHcp, formatId, allowance);
  const nets = useNet ? netScoresFromGross(gross, ph, sis) : gross;
  let thru = 0;
  let val = 0;
  ctx.holes.forEach((h, i) => {
    const s = nets[i];
    if (s == null || !Number.isFinite(s)) return;
    thru++;
    val += (s as number) - h.par;
  });
  return thru === 0 ? null : val;
}

export function toParLabel(v: number): string {
  if (v === 0) return 'E';
  return v > 0 ? `+${v}` : String(v);
}

export function stablefordPts(netVsPar: number): number {
  if (netVsPar <= -3) return 5;
  if (netVsPar === -2) return 4;
  if (netVsPar === -1) return 3;
  if (netVsPar === 0) return 2;
  if (netVsPar === 1) return 1;
  return 0;
}

export function playerNetOnHole(
  player: RoundPlayer,
  holeIx: number,
  holes: CourseHole[],
  scores: (number | null)[],
  formatId: string,
  allPlayers?: RoundPlayer[],
  allowanceOverride?: number | null,
): number | null {
  const g = scores[holeIx];
  if (g == null) return null;
  const sis = strokeIndexes(holes);
  const ph = allPlayers && allPlayers.length >= 2
    ? groupRelativePlayingHandicap(allPlayers, player, formatId, allowanceOverride)
    : playingHandicap(player.courseHcp, formatId, allowanceOverride);
  const netArr = netScoresFromGross(scores, ph, sis);
  return netArr[holeIx];
}

export function courseHcpForPlayer(handicap: number | null, slope = 130): number {
  return courseHandicap(handicap, slope, 72, 72, 18);
}

export { computeNassau, computeSkins };
